/**
 * Primary-draw mirror derivation.
 *
 * Kept free of component imports so it can be unit-tested directly.
 */

/**
 * Minimal shape of a covered draw (payapp). Declared structurally rather than imported from the
 * magic-report types, since those import from this layer.
 */
export interface DrawLike {
  drawId?: string;
  drawSummary?: unknown;
  budgetSummary?: unknown;
  lineItems?: unknown;
}

/**
 * Derives the primary-draw mirrors (`drawSummary` / `budgetSummary` / `lineItems`) from `draws`.
 *
 * `draws` is the single source of truth for a multi-draw report. Those three fields exist only so
 * sections not yet converted to multi-draw keep working, and they are DERIVED here rather than
 * stored so they can never drift: a component that writes them directly has no effect, because the
 * next read recomputes them from `draws`. That removes a class of silent data loss where an edit
 * written to a mirror was overwritten the next time the mirror was rebuilt from `draws`.
 *
 * Reports with no `draws` (limited reports, and magic reports opened without a draw list) are
 * passed through untouched — for those the singular fields ARE the data.
 */
export function withPrimaryDrawMirrors<T extends { draws?: DrawLike[] }>(state: T): T {
  const draws = state.draws;
  if (!draws?.length) return state;

  // The primary draw is the last entry: draws are ordered oldest application number first.
  const primary = draws[draws.length - 1];
  if (!primary) return state;

  return {
    ...state,
    drawSummary: primary.drawSummary,
    budgetSummary: primary.budgetSummary,
    lineItems: primary.lineItems,
  };
}
